import {useRef, useState, useEffect} from 'react';
import styles from './FoldableListItem.module.scss';
import Text from './Text';
import ICON_OPEN_LIGHT_V4 from '../../../public/static/v4/more/icon-open-light.svg';
import ICON_OPEN_V4 from '../../../public/static/v4/more/icon-open.svg';
import useTheme from '../hook/useTheme';

interface IProps {
  title: string;
  subText?: string;
  contents: string;
  setIsOpened: (isOpened: boolean) => void;
  isOpened: boolean;
  space?: number;
  onClickItem?: (isOpened: boolean) => void;
}

export default function FoldableListItemV4({
  title,
  subText,
  contents,
  setIsOpened,
  isOpened,
  space = 0,
  onClickItem,
}: IProps) {
  const wrapper = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);
  const {isLightMode} = useTheme();
  const [height, setHeight] = useState(0);
  const [isTransitionEnd, setIsTransitionEnd] = useState(false);

  useEffect(() => {
    if (wrapper.current) {
      if (isTransitionEnd) {
        const imagesEl = wrapper.current.querySelectorAll('img');
        for (const el of imagesEl) {
          el.addEventListener(
            'load',
            () =>
              wrapper.current &&
              setHeight(
                wrapper.current.offsetHeight + FOLDABLE_ITEM_PADDING_BOTTOM
              )
          );
        }
      }
      setHeight(
        isOpened
          ? wrapper.current.offsetHeight + FOLDABLE_ITEM_PADDING_BOTTOM
          : 0
      );
    }
  }, [isTransitionEnd, isOpened]);

  useEffect(() => {
    if (focusRef.current && isTransitionEnd) {
      window.scrollTo({
        top: focusRef.current.offsetTop - space - FOLDABLE_ITEM_MARGIN_TOP,
        behavior: 'smooth',
      });
    }
  }, [isTransitionEnd, space]);

  return (
    <div
      className="block p-18 rounded-12 bg-grey-01 light:bg-white"
      ref={focusRef}
    >
      <div
        onClick={() => {
          onClickItem?.(isOpened);
          setIsTransitionEnd(false);
          setIsOpened(!isOpened);
        }}
        className="flex justify-between items-center cursor-pointer"
      >
        <div className="flex-1">
          <Text className="leading-18 s13-regular-white light:text-black">
            {title}
          </Text>
          {subText && (
            <Text className="mt-8 s12-regular-grey06">{subText}</Text>
          )}
        </div>
        <img
          alt={!isOpened ? 'close' : 'open'}
          width={20}
          height={20}
          src={isLightMode ? ICON_OPEN_LIGHT_V4 : ICON_OPEN_V4}
          className={cn('flex-none ml-16', {
            'rotate-180': !isOpened,
          })}
        />
      </div>
      <div
        className="overflow-hidden transition-[height] duration-200 ease-in-out"
        style={{height: `${height}px`}}
        onTransitionEnd={() => setIsTransitionEnd(true)}
      >
        <div
          ref={wrapper}
          className={cn(
            'leading-20 -tracking-[0.2px]',
            'pt-18 mt-18',
            'border-t border-solid border-grey-03',
            's13-regular-grey12',
            styles.noticeWrapper
          )}
          dangerouslySetInnerHTML={{
            __html: contents,
          }}
        />
      </div>
    </div>
  );
}

const FOLDABLE_ITEM_MARGIN_TOP = 62;
const FOLDABLE_ITEM_PADDING_BOTTOM = 18;
